import { MEDICAL_SERVICES } from '@/shared/lib/types/utils';

export const removeDashes = (str: string) => {
  return str.replaceAll('-', ' ');
};

/**
 * Escapes HTML-special characters so externally-sourced values can be safely interpolated
 * into a string that is later rendered as HTML (e.g. via html-react-parser in ConfirmDialog).
 */
export const escapeHtml = (str: string | undefined | null) => {
  return (str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

export const isStringDefined = (str: string | undefined | null) => {
  return str && str.length > 0;
};

export const unknown = () => {
  return '---';
};

export const emptyString = () => {
  return ' ';
};

export const truncateText = (text: string | null | undefined, maxLength: number): string => {
  const value = text ?? '';
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

export const invariantCultureIgnoreCase = (str1: string, str2: string) => {
  return str1.localeCompare(str2, undefined, { sensitivity: 'base' }) === 0;
};

export const capitalizeLetterBeforeCharacter = (str: string | undefined) => {
  return str?.replace(/([a-z])([A-Z])/g, '$1 $2');
};

// Must match the `(000) 000-0000` IMask pattern used by TruentityPhoneNumber's editable input —
// a mismatched literal (e.g. a dash where the mask expects a space) makes IMask fail to parse the
// value fed back in via its `value` prop, which clears the field right after the user finishes typing.
export const phoneNumberFormat = (str: string | undefined) => {
  return str?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
};

export const removeNonNumeric = (str: string | null | undefined) => {
  return str?.replace(/\D/g, '');
};

export const convertToSentenceCase = (str: string | undefined) => {
  if (!str) return '';

  const lowerCaseText = str.toLowerCase();

  return lowerCaseText.charAt(0).toUpperCase() + lowerCaseText.slice(1);
};

export const convertToTitleCase = (input: string): string => {
  return input
    .split('_')
    .map(word => {
      if (MEDICAL_SERVICES.has(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

export const capitalizeLetterBeforeCharacterAddSpace = (str: string | null | undefined) => {
  return str?.replace(/([a-z])([A-Z])/g, '$1 $2')?.replace(/\b\w/g, match => match?.toUpperCase());
};

export const parseStringOrDefault = (value: string | null | undefined): string => {
  return value ? value.trim() : '';
};

export const isString = (input: unknown) => typeof input === 'string' || input instanceof String;
export const containsOnlyDigits = (input: string): boolean => /^\d+$/.test(input);

/**
 * Convert any string into a kebab-case slug:
 *   - lowercased
 *   - any run of non-alphanumeric characters becomes a single hyphen
 *   - no leading or trailing hyphens
 *
 * Examples:
 *   toKebabCase('Pending Consent')       => 'pending-consent'
 *   toKebabCase('dg-foo--bar  baz')      => 'dg-foo-bar-baz'
 *   toKebabCase('--Hello, World!--')     => 'hello-world'
 */
export const toKebabCase = (text: string) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const formatFromSnakeCase = (text: string) => {
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const toSnakeCase = (text: string) => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_')
    .toLowerCase();
};

export const removeSpaces = (text: string): string => text.replace(/\s+/g, '');

export const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
};

export const normalizeRawPercentages = (percentages: number[], decimals = 1): number[] => {
  const factor = Math.pow(10, decimals);

  const truncated = percentages.map(p => Number((Math.floor(p * factor) / factor).toFixed(decimals)));

  const truncatedTotal = truncated.reduce((sum, val) => sum + val, 0);
  const difference = Number((100 - truncatedTotal).toFixed(decimals));

  const remainders = percentages.map((original, i) => ({
    index: i,
    remainder: original - truncated[i]
  }));

  remainders.sort((a, b) => b.remainder - a.remainder);

  let unitsToAdd = Math.round(difference * factor);
  for (let i = 0; i < remainders.length && unitsToAdd > 0; i++) {
    truncated[remainders[i].index] = Number((truncated[remainders[i].index] + 1 / factor).toFixed(decimals));
    unitsToAdd--;
  }

  return truncated;
};
